import "./App.css";

import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";

import Home from "./pages/Home";
import LibraryRedirect from "./pages/Home/LibraryRedirect";
import Product from "./pages/Product";
import Authorization from "./pages/Authorization";
import Profile from "./pages/Profile";
import ProtectedRoute from "./pages/ProtectedRoute";
import AuthRoute from "./pages/AuthRoute";
import Layout from "./components/Layout";
import EditProfile from "./pages/EditProfile/EditProfile";

import AffiliateProgram from "./pages/AffiliateProgram/AffiliateProgram";
import NotFound from "./pages/NotFound/NotFound";
import Faq from "./pages/Faq/Faq";
import HowTo from "./pages/HowTo/HowTo";
import FinishCheckout from "./pages/Checkout/FinishCheckout";
import ScrollToTop from "./hooks/ScrollToTop";
import RouteMeta from "./components/RouteMeta";
import AnalyticsRouteTracker from "./analytics/AnalyticsRouteTracker";
import AnalyticsIdentity from "./analytics/AnalyticsIdentity";
import CreatorProfile from "./pages/CreatorProfile";
import TermsOfUse from "./components/Footer/TermsOfUse";
import PrivacyPolicy from "./components/Footer/PrivacyPolicy";
import TopContributors from "./pages/TopContributors/TopContributors";
import Tutorials from "./pages/Tutorials/Tutorials";
import DetailTutorialV2 from "./pages/Tutorials/DetailTutorialV2";
import News from "./pages/News/News";
import DetailNews from "./pages/News/DetailNews";
import GoogleServicePage from "./pages/ServicePage/GoogleServicePage";
import FaceBookServicePage from "./pages/ServicePage/FaceBookServicePage";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentRedirect from "./pages/PaymentRedirect";
import PaddleTest from "./pages/PaddleTest";
import Glavni from "./pages/Glavni";
import MainPagePreview from "./pages/MainPagePreview";
import Survey from "./pages/Survey/Survey";
import GridCostCalculator from "./pages/GridCostCalculator/GridCostCalculator";

import { RECAPTCHA_SITE_KEY } from "./config";

function App() {
  useEffect(() => {
    // Check if user is returning from payment completion
    const urlParams = new URLSearchParams(window.location.search);
    const paymentCompleted = urlParams.get('payment_completed');

    if (paymentCompleted === 'true') {
      // Remove the parameter from URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);

      // Redirect to profile page
      window.location.href = '/profile';
    }
  }, []);

  return (
    <GoogleReCaptchaProvider
      hideBadge={true}
      reCaptchaKey={RECAPTCHA_SITE_KEY}
      scriptProps={{ async: true, defer: true }}
    >
      <Router>
        <ScrollToTop />
        <RouteMeta />
        <AnalyticsRouteTracker />
        <AnalyticsIdentity />
        <Routes>
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="checkout/payment"
            element={
              <ProtectedRoute>
                <FinishCheckout />
              </ProtectedRoute>
            }
          />
          <Route
            path="payment/success"
            element={
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="payment/redirect"
            element={
              <ProtectedRoute>
                <PaymentRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="*"
            element={
              <Layout>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="main-preview" element={<MainPagePreview />} />
                  <Route path="library" element={<LibraryRedirect />} />
                  <Route
                    path="library/product/:productId"
                    element={<Product />}
                  />
                  <Route
                    path="affiliate-program"
                    element={<AffiliateProgram />}
                  />
                  <Route
                    path="authorization"
                    element={
                      <AuthRoute>
                        <Authorization />
                      </AuthRoute>
                    }
                  />
                  <Route
                    path="registration"
                    element={
                      <AuthRoute>
                        <Authorization />
                      </AuthRoute>
                    }
                  />
                  <Route
                    path="reset-password"
                    element={
                      <AuthRoute>
                        <Authorization />
                      </AuthRoute>
                    }
                  />
                  <Route path="profile/:profileId" element={<CreatorProfile />} />
                  <Route path="profile" element={<Profile />} />
                  <Route
                    path="profile/edit"
                    element={
                      <ProtectedRoute>
                        <EditProfile />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="faq" element={<Faq />} />
                  <Route path="how-it-works" element={<HowTo />} />
                  <Route path="grid-cost-calculator" element={<GridCostCalculator />} />
                  <Route path="feedback-survey-en" element={<Survey lang="en" />} />
                  <Route path="feedback-survey-ru" element={<Survey lang="ru" />} />
                  <Route path="top-contributors" element={<TopContributors />} />
                  <Route path="tutorials/:slug" element={<DetailTutorialV2 />} />
                  <Route path="tutorials" element={<Tutorials />} />
                  <Route path="news/:slug" element={<DetailNews />} />
                  <Route path="news" element={<News />} />
                  <Route path="glavni" element={<Glavni />} />
                  <Route
                    path="/google/oauth/callback/"
                    element={<GoogleServicePage />}
                  />
                  <Route
                    path="/facebook/oauth/callback/"
                    element={<FaceBookServicePage />}
                  />
                  <Route path="/paddle-test" element={<PaddleTest />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Layout>
            }
          />
        </Routes>
      </Router>
    </GoogleReCaptchaProvider>
  );
}

export default App;
